import type { CompletedRenderReceipt, IndiiVideoProject } from '@indii/shared';

import { useVideoEditorStore } from '@/modules/creative/video/store/videoEditorStore';
import { queueCloudRender, waitForCloudRender, type CloudRenderDependencies } from './CloudVideoRenderService';

interface DesktopVideoApi {
    getDefaultPath?(filename?: string): Promise<string>;
    render(config: {
        compositionId: string;
        outputLocation: string;
        inputProps: { project: IndiiVideoProject };
    }): Promise<string>;
}

export interface LocalVideoRenderOptions {
    outputLocation?: string;
    outputName?: string;
    organizationId?: string;
}

export interface LocalVideoRenderDependencies {
    videoApi?: DesktopVideoApi;
    /** Web path: durable cloud queue + poll. Defaults to the real services. */
    cloud?: CloudRenderDependencies;
    now: () => number;
    createRenderId: () => string;
    recordArtifact: (
        receipt: CompletedRenderReceipt,
        project: IndiiVideoProject,
        organizationId?: string,
    ) => Promise<void>;
}

const safeOutputName = (project: IndiiVideoProject, requested: string | undefined, now: number): string => {
    const source = requested?.trim() || `${project.name || 'video'}-${now}.mp4`;
    const basename = source.split(/[\\/]/).pop() || `video-${now}.mp4`;
    const sanitized = basename.replace(/[^a-z0-9._-]/gi, '_');
    return sanitized.toLowerCase().endsWith('.mp4') ? sanitized : `${sanitized}.mp4`;
};

const fileUrl = (output: string): string => output.startsWith('file://') ? output : `file://${output}`;

const defaultDependencies = async (): Promise<LocalVideoRenderDependencies> => {
    const { useStore } = await import('@/core/store');
    const owner = useStore.getState().user?.uid;
    const videoApi = typeof window !== 'undefined' ? window.electronAPI?.video : undefined;

    return {
        ...(videoApi?.render ? { videoApi } : {}),
        now: () => Date.now(),
        createRenderId: () => crypto.randomUUID(),
        recordArtifact: async (receipt, project, organizationId) => {

            try {
                const appStore = useStore.getState();
                if (appStore.user?.uid !== owner || appStore.currentProjectId !== project.id || (organizationId && appStore.currentOrganizationId !== organizationId)) return;
                if (useVideoEditorStore.getState().project === project) useVideoEditorStore.getState().setPreviewArtifactUrl(receipt.asset.url);
                appStore.addToHistory?.({
                    id: `export_${receipt.renderId}`,
                    type: 'video',
                    url: receipt.asset.url,
                    ...(receipt.asset.url.startsWith('file://') ? { localPath: receipt.asset.url.replace(/^file:\/\//, '') } : {}),
                    origin: 'editor',
                    prompt: `Export of ${project.name || 'Project'}`,
                    timestamp: Date.now(),
                    projectId: project.id,
                    orgId: organizationId ?? appStore.currentOrganizationId,
                });
            } catch (error) {
                // The render already completed and remains available in preview.
                // Optional library bookkeeping must not turn that success into a failure.
                console.warn('[LocalVideoProjectRenderer] Could not add render to history:', error);
            }
        },
    };
};

/**
 * The single renderer-process entry point for an agent or editor requesting a
 * project render. Desktop routes through Electron (RenderPlanner selects
 * FFmpeg or HyperFrames behind the shared contract); every other surface —
 * web included — queues through the durable cloud render protocol and waits
 * for the completed artifact.
 */
export async function renderVideoProjectLocally(
    project: IndiiVideoProject,
    options: LocalVideoRenderOptions = {},
    dependencies?: LocalVideoRenderDependencies,
): Promise<CompletedRenderReceipt> {
    if (!project.clips.length) {
        throw new Error('The current video project has no clips to render.');
    }

    const deps = dependencies ?? await defaultDependencies();
    const startedAt = deps.now();
    const outputName = safeOutputName(project, options.outputName, startedAt);

    if (deps.videoApi?.render) {
        const outputLocation = options.outputLocation ?? await (() => {
            if (!deps.videoApi?.getDefaultPath) {
                throw new Error('The desktop app could not resolve its managed video folder.');
            }
            return deps.videoApi.getDefaultPath(outputName);
        })();
        const renderedPath = await deps.videoApi.render({
            compositionId: project.id,
            outputLocation,
            inputProps: { project },
        });
        const receipt: CompletedRenderReceipt = {
            status: 'completed',
            renderId: deps.createRenderId(),
            projectId: project.id,
            progress: 100,
            asset: {
                url: fileUrl(renderedPath),
                expiresAt: Number.MAX_SAFE_INTEGER,
                generation: `local-${startedAt}`,
                mimeType: 'video/mp4',
            },
        };
        await deps.recordArtifact(receipt, project, options.organizationId);
        return receipt;
    }

    // Web / cloud path: queue, poll, record. Same receipt contract.
    const cloud = deps.cloud ?? {};
    const { renderId } = await queueCloudRender(project.id, outputName, cloud);
    const artifact = await waitForCloudRender(renderId, {}, cloud);
    const receipt: CompletedRenderReceipt = {
        status: 'completed',
        renderId,
        projectId: project.id,
        progress: 100,
        asset: {
            url: artifact.url,
            expiresAt: startedAt + 24 * 60 * 60 * 1000,
            generation: artifact.generation,
            mimeType: 'video/mp4',
        },
    };
    await deps.recordArtifact(receipt, project, options.organizationId);
    return receipt;
}
